import React from "react";
import { Box, Chip, InputAdornment, TextField, Tooltip, makeStyles } from "@material-ui/core";
import { Icon } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import { istToday } from "../AgentAnalytics/fleetRange";
import { PRESETS, isValidRange } from "./range";

const BANDS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "free_only", label: "Free only" },
  { key: "free_exhausted", label: "Free exhausted" },
  { key: "never_launched", label: "Never launched" },
];

export const FORMATS = [
  { key: "", label: "All formats" },
  { key: "image", label: "Image" },
  { key: "video", label: "Video" },
];

const useStyles = makeStyles({
  bar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  search: {
    minWidth: 340,
    background: tokens.surface,
    borderRadius: tokens.radiusInput,
    "& .MuiOutlinedInput-root": { borderRadius: tokens.radiusInput },
    "& .MuiOutlinedInput-input": {
      fontFamily: tokens.fontBody,
      fontSize: 13,
      padding: "9px 10px",
    },
  },
  chip: {
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    color: tokens.textSecondary,
    fontFamily: tokens.fontBody,
    fontSize: 12,
    fontWeight: 600,
  },
  chipOn: {
    background: tokens.primaryLight,
    border: "1px solid #B9D6D1",
    color: tokens.primary,
    fontWeight: 700,
    "&:hover, &:focus": { background: tokens.primaryLight },
  },
  dateField: {
    background: tokens.surface,
    borderRadius: tokens.radiusInput,
    "& .MuiOutlinedInput-root": { borderRadius: tokens.radiusInput },
    "& .MuiOutlinedInput-input": {
      fontFamily: tokens.fontBody,
      fontSize: 12.5,
      padding: "7px 9px",
    },
  },
  divider: {
    width: 1,
    height: 18,
    background: tokens.border,
    margin: "0 4px",
  },
  dash: {
    fontFamily: tokens.fontBody,
    fontSize: 12,
    color: tokens.textHint,
  },
  invalid: {
    fontFamily: tokens.fontBody,
    fontSize: 11.5,
    fontWeight: 600,
    color: tokens.error,
  },
});

export function FormatChips({ format = "", onFormat }) {
  const classes = useStyles();
  return (
    <>
      {FORMATS.map((f) => (
        <Chip
          key={f.key || "all"}
          size="small"
          clickable
          label={f.label}
          data-testid={`format-chip-${f.key || "all"}`}
          onClick={() => onFormat(f.key)}
          className={`${classes.chip} ${format === f.key ? classes.chipOn : ""}`}
        />
      ))}
    </>
  );
}

export function RangeChips({
  preset,
  onPreset,
  custom = {},
  onCustom,
  maxDate = istToday(),
}) {
  const classes = useStyles();
  const showCustom = preset === "custom";
  const invalid = showCustom && !isValidRange(custom.from, custom.to);

  return (
    <>
      {PRESETS.map((p) => (
        <Chip
          key={p.key}
          size="small"
          clickable
          label={p.label}
          data-testid={`range-chip-${p.key}`}
          onClick={() => onPreset(p.key)}
          className={`${classes.chip} ${preset === p.key ? classes.chipOn : ""}`}
        />
      ))}

      {showCustom ? (
        <Box display="flex" alignItems="center" gridGap={6}>
          <TextField
            type="date"
            variant="outlined"
            size="small"
            className={classes.dateField}
            value={custom.from || ""}
            inputProps={{ "data-testid": "custom-from", max: custom.to || maxDate }}
            onChange={(e) => onCustom({ ...custom, from: e.target.value })}
          />
          <span className={classes.dash}>→</span>
          <TextField
            type="date"
            variant="outlined"
            size="small"
            className={classes.dateField}
            value={custom.to || ""}
            inputProps={{ "data-testid": "custom-to", min: custom.from || "", max: maxDate }}
            onChange={(e) => onCustom({ ...custom, to: e.target.value })}
          />
          {invalid ? (
            <Tooltip title="Showing the last 7 days until both dates are valid">
              <span className={classes.invalid} data-testid="custom-invalid">
                Check dates
              </span>
            </Tooltip>
          ) : null}
        </Box>
      ) : null}
    </>
  );
}

export default function FilterBar({
  search,
  onSearch,
  band,
  onBand,
  preset,
  onPreset,
  custom,
  onCustom,
  showBands = true,
  searchPlaceholder = "Project name, project id or client id",
  showSearch = true,
  format = "",
  onFormat,
  showFormat = false,
}) {
  const classes = useStyles();
  return (
    <Box className={classes.bar}>
      {showSearch && (
        <TextField
          className={classes.search}
          variant="outlined"
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          inputProps={{ "data-testid": "search-input" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon name="search" size={16} style={{ color: tokens.textHint }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      {showBands &&
        BANDS.map((b) => (
          <Chip
            key={b.key}
            size="small"
            clickable
            label={b.label}
            data-testid={`band-chip-${b.key}`}
            onClick={() => onBand(b.key)}
            className={`${classes.chip} ${band === b.key ? classes.chipOn : ""}`}
          />
        ))}
      {showFormat && (
        <>
          {showBands && <span className={classes.divider} />}
          <FormatChips format={format} onFormat={onFormat} />
        </>
      )}
      <Box flexGrow={1} />
      <RangeChips
        preset={preset}
        onPreset={onPreset}
        custom={custom}
        onCustom={onCustom}
      />
    </Box>
  );
}
